import { useMemo } from "react";
import { create } from "zustand";
import { type SubscriptionNotice, subscriptionNotice } from "../subscription/view";
import { useNow } from "./use-live-board";
import { useMySubscription } from "./use-my-subscription";
import { useCan } from "./use-navigation";

/**
 * Banners closed in this run of the app, by kind. Not stored: the reminder
 * comes back on each app start (platform-billing.md, Subscription states).
 */
const useClosed = create<{ closed: Set<SubscriptionNotice["kind"]> }>(() => ({
	closed: new Set(),
}));

/**
 * The owner's subscription banner (P10-T17): past due, or the last three days
 * of a trial. Null for other roles (they cannot pay), while loading, and once
 * closed until the app starts again. `close` hides it for this run.
 */
export function useSubscriptionNotice(): {
	notice: SubscriptionNotice | null;
	close: () => void;
} {
	const owner = useCan("subscription.view");
	const billing = useMySubscription(owner);
	const now = useNow(60_000);
	const closed = useClosed((s) => s.closed);
	const notice = useMemo(
		() => subscriptionNotice(billing.subscription, new Date(now)),
		[billing.subscription, now],
	);
	return {
		notice: notice && !closed.has(notice.kind) ? notice : null,
		close: () => {
			if (!notice) return;
			useClosed.setState((s) => ({ closed: new Set([...s.closed, notice.kind]) }));
		},
	};
}
